/**
 * Suspension Banner Component
 * Modular design with clear structure and all required information
 */

import { useState, useEffect } from 'react';
import { AlertCircle, Download, Clock, X, Loader2, Mail, MessageSquare } from 'lucide-react';
import { useSuspensionStatus } from '@hooks/useSuspensionStatus';
import { downloadAllDataAsZip, DownloadProgress } from '@services/download-all.service';
import { useToast } from '@components/ui';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@config/constants';

const SUPPORT_EMAIL = 'info@fyle-cloud.com';

/**
 * Countdown display component
 */
const CountdownDisplay: React.FC<{ 
  daysRemaining: number | null; 
  isExpired: boolean;
  autoDelete: boolean;
}> = ({ daysRemaining, isExpired, autoDelete }) => {
  if (isExpired) {
    if (autoDelete) {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <Clock className="w-4 h-4" />
          <span className="font-semibold">Download period expired - Account may be deleted</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <Clock className="w-4 h-4" />
          <span className="font-semibold">Download period expired</span>
        </div>
      );
    }
  }

  if (daysRemaining === null) {
    return null;
  }

  if (autoDelete) {
    // Show deletion countdown (Storage-Overflow after downgrade)
    return (
      <div className="flex items-center gap-2 text-red-700">
        <Clock className="w-4 h-4 text-red-600" />
        <span className="font-medium">
          {daysRemaining === 0 
            ? '⚠️ Last day - Account will be permanently deleted tomorrow' 
            : `⚠️ ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining until account deletion`}
        </span>
      </div>
    );
  } else {
    // Show download access countdown (Terms violation - admin review)
    return (
      <div className="flex items-center gap-2 text-gray-700">
        <Clock className="w-4 h-4 text-blue-600" />
        <span className="font-medium">
          {daysRemaining === 0 
            ? 'Last day to download your data' 
            : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining to download your data`}
        </span>
      </div>
    );
  }
};

/**
 * Download button component
 */
const DownloadButton: React.FC<{ 
  onDownload: () => void; 
  isDownloading: boolean; 
  disabled: boolean;
  progress: DownloadProgress | null;
}> = ({ onDownload, isDownloading, disabled, progress }) => {
  return (
    <div className="space-y-2">
      <button
        onClick={onDownload}
        disabled={disabled || isDownloading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed disabled:hover:shadow-md"
      >
        {isDownloading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>
              {progress?.stage === 'fetching' && 'Fetching files...'}
              {progress?.stage === 'downloading' && `Downloading... ${progress.current}/${progress.total}`}
              {progress?.stage === 'packaging' && 'Creating ZIP...'}
              {progress?.stage === 'complete' && !progress.message.startsWith('Error:') && 'Download started!'}
              {!progress && 'Preparing ZIP...'}
            </span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>Download All Files as ZIP</span>
          </>
        )}
      </button>
      {progress && progress.total > 0 && progress.stage !== 'complete' && (
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${(progress.current / progress.total) * 100}%` }}
          />
        </div>
      )}
      {progress?.stage === 'complete' && !progress.message.startsWith('Error:') && (
        <p className="text-xs text-green-700 font-medium flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Download started! Check your downloads folder.
        </p>
      )}
    </div>
  );
};

/**
 * Contact email link component
 */
const ContactLink: React.FC = () => {
  return (
    <a
      href={`mailto:${SUPPORT_EMAIL}`}
      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
    >
      <Mail className="w-4 h-4" />
      <span>{SUPPORT_EMAIL}</span>
    </a>
  );
};

/**
 * Support ticket link component
 */
const SupportTicketLink: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate(ROUTES.SUPPORT)}
      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
    >
      <MessageSquare className="w-4 h-4" />
      <span>Create Support Ticket</span>
    </button>
  );
};

/**
 * Hook to manage banner dismissal state
 * Banner is always shown on page load, dismissal only persists for current session
 */
const useBannerDismissal = () => {
  // Always start with banner visible (not dismissed)
  const [isDismissed, setIsDismissedState] = useState(false);

  useEffect(() => {
    const handleReopen = () => {
      setIsDismissedState(false);
    };

    window.addEventListener('suspension-banner-reopen', handleReopen);
    return () => {
      window.removeEventListener('suspension-banner-reopen', handleReopen);
    };
  }, []);

  const setIsDismissed = (value: boolean) => {
    setIsDismissedState(value);
    // Don't persist to localStorage - dismissal only for current session
    // This ensures banner is always visible when page is opened
  };

  return { isDismissed, setIsDismissed };
};

/**
 * Main Suspension Banner Component
 */
export const SuspensionBanner: React.FC = () => {
  const { status, isLoading } = useSuspensionStatus();
  const { isDismissed, setIsDismissed } = useBannerDismissal();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const { showToast } = useToast();

  // Don't show if loading, not suspended, no download access, or dismissed
  if (
    isLoading ||
    !status ||
    !status.isSuspended ||
    !status.downloadEnabled ||
    isDismissed
  ) {
    return null;
  }

  const handleDownloadAll = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress({
      stage: 'fetching',
      current: 0,
      total: 0,
      message: 'Preparing download...',
    });

    try {
      showToast('info', 'Preparing download...', 'Collecting all your files into a ZIP archive.');
      
      await downloadAllDataAsZip((progress: DownloadProgress) => {
        setDownloadProgress(progress);
        
        // Update toast with progress
        if (progress.stage === 'downloading' && progress.total > 0) {
          showToast('info', 'Downloading files...', `${progress.current} of ${progress.total} files downloaded`);
        } else if (progress.stage === 'packaging') {
          showToast('info', 'Packaging files...', 'Creating ZIP archive...');
        }
      });

      showToast('success', 'Download started!', 'Your ZIP file is downloading. Check your downloads folder.');
      
      // Clear progress after success
      setTimeout(() => {
        setDownloadProgress(null);
      }, 3000);
    } catch (error: any) {
      console.error('Error downloading all data:', error);
      showToast('error', 'Download failed', error.message || 'Failed to download your data. Please try again.');
      setDownloadProgress({
        stage: 'complete',
        current: 0,
        total: 0,
        message: `Error: ${error.message || 'Failed to download'}`,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-white border-b border-gray-200 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <div className="mb-3">
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                Account Suspended - Download Access Active
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {status.autoDelete 
                  ? 'Your account has been suspended due to storage limit exceeded after a plan downgrade. You have limited time to download your data before the account is permanently deleted.'
                  : 'Your account has been temporarily suspended while an admin reviews your case. You can download your data during this period.'}
              </p>
              {status.autoDelete && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                  <p className="text-xs text-red-800 font-medium">
                    ⚠️ After the download period expires, your account and all data will be permanently deleted and cannot be recovered.
                  </p>
                </div>
              )}
            </div>

            {/* Countdown */}
            <div className="mb-4">
              <CountdownDisplay 
                daysRemaining={status.daysRemaining} 
                isExpired={status.isExpired}
                autoDelete={status.autoDelete}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-4 mb-3">
              {!status.isExpired && (
                <DownloadButton
                  onDownload={handleDownloadAll}
                  isDownloading={isDownloading}
                  disabled={false}
                  progress={downloadProgress}
                />
              )}
              <div className="flex flex-wrap items-center gap-3">
                <ContactLink />
                <span className="text-xs text-gray-500">or</span>
                <SupportTicketLink />
                <span className="text-xs text-gray-500">- For questions, please contact us via email or create a support ticket</span>
              </div>
            </div>

            {/* Warning if expired */}
            {status.isExpired && (
              <div className={`mt-3 p-3 rounded-lg border ${
                status.autoDelete 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <p className={`text-sm font-medium ${
                  status.autoDelete 
                    ? 'text-red-800' 
                    : 'text-amber-800'
                }`}>
                  {status.autoDelete 
                    ? '⚠️ The download period has expired. Your account and all data will be permanently deleted soon.'
                    : '⚠️ The download period has expired. An admin will review your case and decide on the next steps. Please contact support if you need assistance.'}
                </p>
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label="Close banner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Export function to clear banner dismissal (used by Header button)
 */
export const clearSuspensionBannerDismissal = () => {
  // Trigger a custom event to notify the banner component
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('suspension-banner-reopen'));
  }
};
